import appStore from 'client/AppStore';
import {callAction} from 'client/framework';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import sessionStore from 'client/Session/SessionStore';
import {fromJS, List, Map} from 'immutable';
import {hasLicenseAccess} from 'lib/UserAccessUtil';

/**
 * The navlinks structure represents, as config, the available links a user will see in the
 *   1. main navigation (`mainLinks`)
 *   2. user drop down menu (`dropdownLinks`)
 *
 * These links will be different if the user is in Albert (`albertLinks`) vs. if they are in dennis (`dennisLinks`).
 *
 * `mainLinks` and `dropdownLinks` under the `albertLinks` and `dennisLinks` keys can
 * be filtered by adding a `permissionPredicates` list.
 *
 * `permissionPredicates` is a list of functions returning true or false that will be OR'd together to determine
 * if the navigation item should be shown to a user of the site. Only one predicate needs to return true.
 */

type ListOfLinks = List<Map<string, any>>;
type NavMap = Map<string, Map<string, ListOfLinks>>;
const navLinks: NavMap = fromJS({
  albertLinks: {
    mainLinks: [
      {
        key: 'library',
        name: 'Library',
        route: '/subjects',
        matchingRoutes: ['/subjects'],
        permissionPredicates: [
          ({isTeacherWithClassSubjects}) =>
            sessionStore.isStudent() ||
            (sessionStore.isAuthor() && !sessionStore.hasTeacherAccess()) ||
            (sessionStore.isTeacher() && !isTeacherWithClassSubjects)
        ]
      },
      {
        key: 'library',
        name: 'Library',
        route: '/subjects/class-subjects',
        matchingRoutes: ['/subjects'],
        permissionPredicates: [
          ({isTeacherWithClassSubjects}) => sessionStore.isTeacher() && isTeacherWithClassSubjects
        ]
      },
      {
        key: 'classes',
        name: 'Classes',
        route: '/classes'
      },
      {
        key: 'reports',
        name: 'Reports',
        route: '/reports',
        permissionPredicates: [() => sessionStore.hasTeacherAccess()]
      },
      {
        key: 'assignments',
        name: 'Assignments',
        route: '/assignments',
        permissionPredicates: [
          () => sessionStore.hasTeacherAccess() && sessionStore.hasValidSession()
        ],
        routesArr: ['/saved', '/assignments', '/assignment-builder/create-assignment']
      },
      {
        key: 'admin',
        name: 'Admin',
        route: '/school-admin',
        permissionPredicates: [
          () => sessionStore.isSchoolAdministrator(),
          () => sessionStore.isSuper()
        ]
      },
      {
        key: 'pricing',
        name: 'Pricing',
        hardLink: true,
        route: '/pricing-schools',
        permissionPredicates: [
          () => {
            const {isReady, value} = hasLicenseAccess();
            return isReady && !value;
          },
          () => sessionStore.isSuper()
        ]
      }
    ],
    dropdownLinks: [
      {
        icon: 'user',
        key: 'account',
        name: 'My Account',
        route: '/account/profile',
        showSeparator: true
      },
      {
        icon: 'chart-line',
        key: 'dashboard',
        name: 'My Subject Analytics',
        route: '/dashboard',
        showSeparator: true
      },
      {
        icon: 'pen-nib',
        key: 'authoring',
        name: 'Authoring',
        route: '/dennis/content/dashboard',
        permissionPredicates: [
          () => sessionStore.isAuthor() && !sessionStore.hasContentManagerAccess()
        ]
      },
      {
        icon: 'wrench',
        key: 'dennis',
        name: 'Dennis',
        route: '/dennis',
        permissionPredicates: [() => sessionStore.hasContentManagerAccess()]
      },
      {
        icon: 'book',
        hardLink: true,
        key: 'resource-hub',
        name: 'Resource Hub',
        route: '/resource-hub/',
        permissionPredicates: [() => sessionStore.hasValidSession() && !sessionStore.isStudent()]
      },
      {
        icon: 'question-circle',
        key: 'help',
        name: 'Help',
        hardLink: true,
        route: 'https://help.albert.io/en/'
      },
      {
        icon: 'comment-pen',
        key: 'feedback',
        name: 'Rate Albert',
        permissionPredicates: [() => sessionStore.isSuper(), () => sessionStore.isTeacher()]
      },
      {
        icon: 'sign-out',
        key: 'logout',
        name: 'Log Out',
        action: () => callAction(sessionLifecycleActions.END_SESSION)
      }
    ]
  },
  dennisLinks: {
    mainLinks: [
      {
        key: 'content',
        name: 'Content',
        route: `/dennis/content`,
        permissionPredicates: [() => sessionStore.hasAuthorAccess()]
      },
      {
        key: 'users',
        name: 'Users',
        route: `/dennis/users`,
        permissionPredicates: [() => sessionStore.isSuper()]
      },
      {
        key: 'districts',
        name: 'Districts',
        route: `/dennis/districts`,
        permissionPredicates: [() => sessionStore.isSuper()]
      },
      {
        key: 'schools',
        name: 'Schools',
        route: `/dennis/schools`,
        permissionPredicates: [() => sessionStore.isSuper()]
      }
    ],
    dropdownLinks: [
      {
        key: 'logout',
        name: 'Log Out',
        action: () => callAction(sessionLifecycleActions.END_SESSION)
      },
      {
        key: 'backToAlbert',
        name: 'Back to Albert',
        route: '/subjects',
        hardLink: true
      }
    ]
  }
});

/**
 * Takes a path, and builds a list of navigation links for that path.
 *
 * It also builds links only for the active zone.
 *
 * This function will prune __OR MODIFY__ the default mainLinks defined
 * in this file depending on a variety of factors. Notably:
 *   1. what path is given to the function
 *   2. what the user's session is
 *
 * @param {string} path
 * @param {Object} options
 * @param {boolean} options.isTeacherWithClassSubjects
 * @returns {ListOfLinks} list of main navigation links based on zone and path
 */
export function getNavigationLinksForPath(
  path: string,
  {isTeacherWithClassSubjects = false}: {isTeacherWithClassSubjects?: boolean}
): ListOfLinks {
  if (appStore.isZoneDennis()) {
    return applyPermissionPredicates(navLinks.get('dennisLinks')!.get('mainLinks'));
  }

  /**
   * The signup route has the main navigation effectively disabled
   */
  const isSignUpPage = path.includes('/signup');
  if (!sessionStore.hasValidSession() && isSignUpPage) {
    return List();
  }

  let finalLinks = navLinks.get('albertLinks').get('mainLinks');

  finalLinks = applyPermissionPredicates(finalLinks, {isTeacherWithClassSubjects});
  return finalLinks;
}

/**
 * Returns dropdown links based on what zone a user is in
 *
 * @returns {ListOfLinks} list of main navigation dropdown  links
 */
export function getDropdownLinks(): ListOfLinks {
  let links: ListOfLinks = List();
  if (appStore.isZoneAlbert()) {
    links = navLinks.getIn(['albertLinks', 'dropdownLinks']);
  } else if (appStore.isZoneDennis()) {
    links = navLinks.getIn(['dennisLinks', 'dropdownLinks']);
  }

  return applyPermissionPredicates(links);
}

/**
 * For a given list of link objects, checks for predicates, and applies them
 *
 * Returns a filtered list of link objects depending on the outcome of the `.some`
 *
 * @param {List<NavLink>} links
 * @param {any} args
 * @returns {ListOfLinks} list of link objects
 */
function applyPermissionPredicates(links: ListOfLinks, args?: any): ListOfLinks {
  // @ts-ignore acc and link are not undefined
  return links.reduce((acc: ListOfLinks, link: Map<string, any>) => {
    if (link.has('permissionPredicates')) {
      const hasPermission = link.get('permissionPredicates').some((predicate) => predicate(args));

      if (link.has('dropdownLinks') && hasPermission) {
        const dropdownLinks = link.get('dropdownLinks').filter((dropdownLink) => {
          if (dropdownLink.has('permissionPredicates')) {
            const predicates = dropdownLink.get('permissionPredicates');
            return predicates.some((predicate) => {
              return predicate();
            });
          }

          return true;
        });

        return acc.push(link.set('dropdownLinks', dropdownLinks));
      }

      return hasPermission ? acc.push(link) : acc;
    }

    return acc.push(link);
  }, List() as ListOfLinks);
}
